import React, { useState, useEffect } from 'react';
import { FaHospital, FaSearch, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import hospitalService from '../services/hospitalService';
import '../styles/HospitalManagement.css';
import '../styles/UserManagement.css';
import '../styles/theme/toast.css';

const HospitalManagement = ({ createMode = false }) => {
    const [hospitals, setHospitals] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(createMode);
    const [currentHospital, setCurrentHospital] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        direccion: '',
        provincia: '',
        tipoInstitucion: '',
        telefono: '',
        correoContacto: ''
    });
    const [errors, setErrors] = useState({});
    const [showToast, setShowToast] = useState(false);

    // Cargar hospitales
    useEffect(() => {
        fetchHospitals(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchHospitals(page, pageSize);
    }, [page, pageSize]);

    // Cargar hospitales desde la API
    const fetchHospitals = async (p = 1, ps = 10) => {
        try {
            const response = await hospitalService.getAllHospitales(p, ps);
            // response may be paginated (response.data = { data, total, ... }) or array
            if (response?.data && Array.isArray(response.data.data)) {
                setHospitals(response.data.data || []);
                setTotal(Number(response.data.total) || 0);
                setTotalPages(Math.max(1, Math.ceil((Number(response.data.total) || 0) / Number(response.data.pageSize || ps))));
            } else if (Array.isArray(response?.data)) {
                setHospitals(response.data);
                setTotal(response.data.length);
                setTotalPages(1);
            } else {
                setHospitals([]);
                setTotal(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error al cargar hospitales:', error);
            setHospitals([]);
            setTotal(0);
            setTotalPages(1);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpiar error cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
        if (!formData.direccion.trim()) newErrors.direccion = 'La dirección es requerida';
        if (!formData.provincia.trim()) newErrors.provincia = 'El país es requerido';
        if (!formData.tipoInstitucion.trim()) newErrors.tipoInstitucion = 'El tipo de institución es requerido';
        if (!formData.telefono.trim()) {
            newErrors.telefono = 'El teléfono es requerido';
        } else if (!/^\d+$/.test(formData.telefono)) {
            newErrors.telefono = 'El teléfono solo puede contener números';
        }
        if (!formData.correoContacto.trim()) {
            newErrors.correoContacto = 'El correo es requerido';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.correoContacto)) {
            newErrors.correoContacto = 'Correo electrónico inválido';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (currentHospital) {
                // Actualizar hospital existente
                await hospitalService.updateHospital(currentHospital.HOSPITAL_ID, formData);
            } else {
                // Crear nuevo hospital
                await hospitalService.createHospital(formData);
            }
            
            await fetchHospitals();
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
                resetForm();
            }, 3000);
        } catch (error) {
            console.error('Error al guardar hospital:', error);
        }
    };

    const handleEdit = (hospital) => {
        setCurrentHospital(hospital);
        setFormData({
            nombre: hospital.HOSPITAL_NOMBRE || '',
            direccion: hospital.HOSPITAL_DIRECCION || '',
            provincia: hospital.HOSPITAL_PAIS || '',
            tipoInstitucion: hospital.HOSPITAL_TIPOINSTITUCION || '',
            telefono: hospital.HOSPITAL_TELEFONO || '',
            correoContacto: hospital.HOSPITAL_CORREO || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar este hospital? Esta acción no se puede deshacer.')) {
            try {
                await hospitalService.deleteHospital(id);
                await fetchHospitals();
            } catch (error) {
                console.error('Error al eliminar hospital:', error);
                alert(error.response?.data?.message || 'Error al eliminar el hospital.');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            nombre: '',
            direccion: '',
            provincia: '',
            tipoInstitucion: '',
            telefono: '',
            correoContacto: ''
        });
        setCurrentHospital(null);
        setShowModal(false);
        setErrors({});
    };

    const filteredHospitals = hospitals.filter(hospital =>
        (hospital.HOSPITAL_NOMBRE || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (hospital.HOSPITAL_DIRECCION || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (hospital.HOSPITAL_TIPOINSTITUCION || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (hospital.HOSPITAL_PAIS || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            {showToast && (
                <div className="custom-toast custom-toast-success">
                    ¡Hospital guardado exitosamente!
                </div>
            )}
            <div className="hospital-management-container">
                <div className="hospital-management-header">
                    <h1>Gestión de Hospitales</h1>
                    <p>Administre los hospitales del sistema</p>
                </div>

                <div className="hospital-management-actions">
                    <div className="search-section">
                        <input
                            type="text"
                            placeholder="Buscar hospital..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <FaSearch className="search-icon" />
                    </div>
                    <button 
                        className="btn-primary register-btn create-btn"
                        onClick={() => {
                            setShowModal(true);
                            setFormData({
                                nombre: '',
                                direccion: '',
                                provincia: '',
                                tipoInstitucion: '',
                                telefono: '',
                                correoContacto: ''
                            });
                            setCurrentHospital(null);
                            setErrors({});
                        }}
                    >
                        <FaPlus /> Nuevo Hospital
                    </button>
                </div>

                <div className="hospitals-table-container">
                    <table className="hospitals-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nombre</th>
                                <th>Dirección</th>
                                                                    <th>País</th>
                                <th>Tipo de Institución</th>
                                <th>Teléfono</th>
                                <th>Correo</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredHospitals.map(hospital => (
                                <tr key={hospital.HOSPITAL_ID}>
                                    <td><span className="code-id-field">{hospital.HOSPITAL_ID}</span></td>
                                    <td>
                                        <div className="hospital-name">
                                            <div className="hospital-icon">
                                                <FaHospital />
                                            </div>
                                            {hospital.HOSPITAL_NOMBRE}
                                        </div>
                                    </td>
                                    <td>{hospital.HOSPITAL_DIRECCION}</td>
                                    <td>{hospital.HOSPITAL_PAIS || 'No especificado'}</td>
                                    <td>
                                        <span className={`status-badge ${hospital.HOSPITAL_TIPOINSTITUCION === 'PUBLICA' ? 'activo' : 'inactivo'}`}>
                                            {hospital.HOSPITAL_TIPOINSTITUCION === 'PUBLICA' ? 'Público' : 'Privado'}
                                        </span>
                                    </td>
                                    <td>{hospital.HOSPITAL_TELEFONO || 'No especificado'}</td>
                                    <td>{hospital.HOSPITAL_CORREO || 'No especificado'}</td>
                                    <td>
                                        <div className="actions">
                                            <button
                                                className="action-btn edit-btn"
                                                onClick={() => handleEdit(hospital)}
                                                title="Editar"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                onClick={() => handleDelete(hospital.HOSPITAL_ID)}
                                                title="Eliminar"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredHospitals.length === 0 && (
                        <div className="table-footer">
                            No se encontraron hospitales
                        </div>
                    )}
                </div>

                <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <div>
                        Mostrando <strong>{filteredHospitals.length}</strong> de <strong>{total || hospitals.length}</strong> hospitales
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
                        <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
                        <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
                        <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>Por página:</span>
                            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </label>
                    </div>
                </div>
            </div>

            {/* Modal flotante para agregar/editar hospital - FUERA del contenedor principal */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <button 
                            className="modal-close-btn" 
                            onClick={() => setShowModal(false)} 
                            title="Cerrar"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        
                        <form onSubmit={async (e) => { 
                            e.preventDefault();
                            try {
                                await handleSubmit(e);
                                setShowModal(false);
                            } catch (error) {
                                // El error ya se maneja en handleSubmit
                            }
                        }} className="hospital-form">
                            <div className="form-section">
                                <h3>
                                    <i className="fas fa-hospital"></i>
                                    {currentHospital ? 'Editar Hospital' : 'Agregar nuevo hospital'}
                                </h3>
                                
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="nombre">Nombre del Hospital</label>
                                        <input
                                            type="text"
                                            id="nombre"
                                            name="nombre"
                                            placeholder="Ingrese el nombre del hospital..."
                                            value={formData.nombre}
                                            onChange={handleInputChange}
                                            className={errors.nombre ? 'error' : ''}
                                            autoFocus
                                            required
                                        />
                                        {errors.nombre && (
                                            <span className="error-message">{errors.nombre}</span>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="direccion">Dirección</label>
                                        <input
                                            type="text"
                                            id="direccion"
                                            name="direccion"
                                            placeholder="Ingrese la dirección completa..."
                                            value={formData.direccion}
                                            onChange={handleInputChange}
                                            className={errors.direccion ? 'error' : ''}
                                            required
                                        />
                                        {errors.direccion && (
                                            <span className="error-message">{errors.direccion}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="provincia">País</label>
                                        <input
                                            type="text"
                                            id="provincia"
                                            name="provincia"
                                            placeholder="País..."
                                            value={formData.provincia}
                                            onChange={handleInputChange}
                                            className={errors.provincia ? 'error' : ''}
                                            required
                                        />
                                        {errors.provincia && (
                                            <span className="error-message">{errors.provincia}</span>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="tipoInstitucion">Tipo de Institución</label>
                                        <select
                                            id="tipoInstitucion"
                                            name="tipoInstitucion"
                                            value={formData.tipoInstitucion}
                                            onChange={handleInputChange}
                                            className={errors.tipoInstitucion ? 'error' : ''}
                                            required
                                        >
                                            <option value="">Seleccione un tipo</option>
                                            <option value="PUBLICA">Público</option>
                                            <option value="PRIVADA">Privado</option>
                                        </select>
                                        {errors.tipoInstitucion && (
                                            <span className="error-message">{errors.tipoInstitucion}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="telefono">Teléfono</label>
                                        <input
                                            type="tel"
                                            id="telefono"
                                            name="telefono"
                                            placeholder="(02) 2000-000"
                                            value={formData.telefono}
                                            onChange={handleInputChange}
                                            className={errors.telefono ? 'error' : ''}
                                            required
                                        />
                                        {errors.telefono && (
                                            <span className="error-message">{errors.telefono}</span>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="correoContacto">Correo de Contacto</label>
                                        <input
                                            type="email"
                                            id="correoContacto"
                                            name="correoContacto"
                                            placeholder="contacto@hospital.com"
                                            value={formData.correoContacto}
                                            onChange={handleInputChange}
                                            className={errors.correoContacto ? 'error' : ''}
                                            required
                                        />
                                        {errors.correoContacto && (
                                            <span className="error-message">{errors.correoContacto}</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    <i className="fas fa-plus"></i> {currentHospital ? 'Guardar Cambios' : 'Crear Hospital'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default HospitalManagement;